<template>
  <Card class="w-full">
    <CardHeader>
      <CardTitle class="text-[1.1rem]">Comentários</CardTitle>
    </CardHeader>
    <CardContent>
      <div class="space-y-4" v-if="comments && comments.data.length > 0">
        <ProfileCommentComponent v-for="comment in comments.data" :key="comment.id" :comment="comment"
          @delete="getComments" />
      </div>
      <CommentsSkeleton v-else-if="isFetching" />
      <div v-else-if="!isFetching && (!comments || comments.data.length === 0)" class="py-10 text-center">
        <p class="text-gray-400">Nenhum comentário ainda.</p>
      </div>
      <PaginationApi v-if="comments" :data="comments?.pagination" :paginate="getComments" />
      <form @submit.prevent="createComment" class="mt-6">
        <Textarea v-model="payload.content" placeholder="Escreva um comentário..." :rows="3" class="mb-2" />
        <Button :disabled="!payload.content || isCreatingComment" type="submit">
          <Loader2 v-if="isCreatingComment" class="w-4 h-4 mr-2 animate-spin" />
          Adicionar
        </Button>
      </form>
    </CardContent>
  </Card>
</template>

<script setup lang="ts">
import { onMounted, ref, watch } from 'vue'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import { useToast } from '@/components/ui/toast'
import { useAxios } from '@/composables/useAxios'
import { Profile } from '@/interfaces/profiles/Profile'
import { Pagination } from '@/interfaces/Pagination'
import { ProfileComment } from '@/interfaces/profiles/ProfileComment'
import ProfileCommentComponent from './components/ProfileComment.vue'
import PaginationApi from '@/components/ui/pagination/PaginationApi.vue'
import CommentsSkeleton from './components/CommentsSkeleton.vue'
import { Loader2 } from 'lucide-vue-next'

const axios = useAxios()
const props = defineProps<{ profile?: Profile, profileId: number | string }>()
const comments = ref<Pagination<ProfileComment>>()
const payload = ref({ content: '' })
const { toast } = useToast()
const isCreatingComment = ref(false)
const isFetching = ref(true)

function getComments(page = 1) {
  const id = props.profile?.id || props.profileId
  console.log('Fetching comments using ID:', id, '(from profile:', props.profile?.id, ', from route:', props.profileId, ')');
  if (!id) {
    console.warn('No ID found for fetching comments');
    return;
  }

  isFetching.value = true
  axios.get(`/profile/${id}/comments`, { params: { page } })
    .then((res) => {
      // Robustly handle both simple arrays and paginated objects
      if (Array.isArray(res.data)) {
        comments.value = {
          data: res.data,
          pagination: { total: res.data.length, lastPage: 1, page: 1 } as any
        }
      } else if (res.data && Array.isArray(res.data.data)) {
        comments.value = res.data
      } else {
        comments.value = { data: [], pagination: {} as any }
      }
    })
    .catch(err => {
      console.error('Error fetching comments:', err);
      comments.value = { data: [], pagination: {} as any }
    })
    .finally(() => {
      isFetching.value = false
    })
}

const createComment = () => {
  const id = props.profile?.id || props.profileId
  if (!id) return;
  isCreatingComment.value = true

  axios.post(`/profile/${id}/comments`, payload.value)
    .then(() => {
      toast({
        title: 'Comentário adicionado com sucesso',
        class: 'bg-green-500 text-white'
      })
      payload.value.content = ''
      getComments()
    })
    .finally(() => isCreatingComment.value = false)
}

watch(() => props.profileId, (newId) => {
  if (newId) {
    getComments()
  }
})

watch(() => props.profile, (newProfile) => {
  if (newProfile && (!comments.value || comments.value.data.length === 0)) {
    getComments()
  }
}, { immediate: true })
</script>